import { useCallback, useState } from "react";
import { useIsMobile } from "./useIsMobile";

export const SIDEBAR_COLLAPSED_KEY = "sidebar_collapsed";

/** Delay before a synthetic resize, matching the rail's 300ms width transition. */
const RESIZE_AFTER_TOGGLE_MS = 320;

function readStoredCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function writeStoredCollapsed(value) {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * The app shell's sidebar collapsed state.
 *
 * Desktop and mobile keep separate state:
 * - Desktop (≥ 640px) uses the persisted `sidebar_collapsed` choice, and a
 *   toggle there is saved.
 * - Mobile (< 640px, `useIsMobile`) always starts collapsed to the icon rail,
 *   because the expanded 14rem rail leaves a 375px phone about 150px of
 *   content. Expanding it there lasts for the session only and is never
 *   saved, so it doesn't overwrite the desktop choice.
 *
 * Crossing the breakpoint switches between the two without losing either.
 *
 * @returns {{ collapsed: boolean, toggleCollapsed: () => void }}
 */
export function useSidebarCollapsed() {
  const isMobile = useIsMobile();
  const [desktopCollapsed, setDesktopCollapsed] = useState(readStoredCollapsed);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const collapsed = isMobile ? !mobileExpanded : desktopCollapsed;

  const toggleCollapsed = useCallback(() => {
    if (isMobile) {
      setMobileExpanded((prev) => !prev);
    } else {
      const next = !desktopCollapsed;
      writeStoredCollapsed(next);
      setDesktopCollapsed(next);
    }
    // Let layout-aware widgets (charts, tables) remeasure after the width transition.
    window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, RESIZE_AFTER_TOGGLE_MS);
  }, [isMobile, desktopCollapsed]);

  return { collapsed, toggleCollapsed };
}

export default useSidebarCollapsed;
